export const REDACTED = '[redacted]'

// Substring match, so compound names like current_password and client_secret are caught too.
const SENSITIVE_KEY =
  /pass(word|code)?|secret|token|api[_-]?key|apikey|private[_-]?key|credential|authorization|auth/i

// Exact match (with common owner prefixes), unlike SENSITIVE_KEY: a substring rule here would
// redact ordinary debugging fields such as email_verified_at or address_type.
// The bare number/name-ish entries match this app's IResponseContact shape, where the phone
// lives on `number`, `national_number`, and `formatted_number`.
const PII_KEY =
  /^(?:contact|customer|user|recipient|sender|billing|shipping|business|work|home|personal|primary|secondary|owner|member|lead)?_?(?:e-?mail(?:_address)?|phones?|phone_number|mobile(?:_number)?|cell|fax|number|national_number|formatted_number|country_calling_code|first_name|last_name|full_name|middle_name|display_name|nickname|address|address_?[12]|street(?:_address)?(?:_?[12])?|city|zip(?:_?code)?|postal(?:_v)?_?code|dob|date_of_birth|birthday|ssn|username|(?:avatar|photo)(?:_url)?|latitude|longitude)$/i

// Email and phone are matched as a whole word anywhere in the key, because the API spells them
// many ways (baa_approved_by_email, businessVerificationEmail, mobile_phone, phone_to_replace)
// and a value in any of them is personal data. Names are not treated this way, see below.
const PII_TOKEN = /(^|_)(?:e-?mails?|phones?)(_|$)/i

// Every _at field in this API is a timestamp, so email_verified_at and the like stay readable
// rather than being caught by the token rule above.
const TIMESTAMP_KEY = /_at$/i

// Address components arrive under an address_ prefix rather than as bare keys.
const ADDRESS_KEY =
  /^(?:billing|shipping|business|home|mailing)?_?address_(?:line_?[12]|street|city|state|zip(?:_code)?|country|postal_?code)$/i

// A bare `name` is left visible: on broadcasts, templates, and integrations it is an entity
// label, not PII. Only a name qualified by a person prefix is redacted.
const PERSON_NAME_KEY = /^(?:contact|customer|user|recipient|sender|owner|member|lead)_name$/i

// The API mixes conventions: contacts are snake_case, the compliance and TCR payloads are
// camelCase (firstName, businessEmail, postalCode). Match one spelling by normalising first.
const normalizeKey = (key: string): string =>
  key.replace(/([a-z0-9])([A-Z])/g, '$1_$2').toLowerCase()

const SENSITIVE_ENDPOINT =
  /auth|2fa|sign-?up|register|reset|forgot|password|login|integration|connect|credential|oauth/i

export const isSensitiveEndpoint = (url?: string): boolean => !!url && SENSITIVE_ENDPOINT.test(url)

// A path segment that looks like a secret token: long and mixing letters with digits, or a UUID.
const OPAQUE_SEGMENT = /^(?=.*[A-Za-z])(?=.*\d)[A-Za-z0-9._~-]{20,}$/
const UUID_SEGMENT = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i

// Tokens can ride in the URL path (e.g. invite links) or the query string. Mask both
// before the URL goes into Sentry tags, fingerprint, or extra.
export function sanitizeUrl(url?: string): string {
  if (!url) return ''

  const [path, query] = url.split('?')
  const cleanedPath = path
    .split('/')
    .map((segment) =>
      OPAQUE_SEGMENT.test(segment) || UUID_SEGMENT.test(segment)
        ? ':redacted'
        : maskPiiInString(segment)
    )
    .join('/')

  return query !== undefined ? `${cleanedPath}?[redacted]` : cleanedPath
}

const EMAIL_VALUE = /[A-Za-z0-9._%+-]+@[A-Za-z0-9.-]+\.[A-Za-z]{2,}/g

// Only formatted or country-coded numbers, so plain ids and timestamps are left alone.
const PHONE_VALUE =
  /\+\d[\d\s().-]{7,}\d|\(\d{3}\)\s?\d{3}[\s.-]?\d{4}|\b\d{3}[\s.-]\d{3}[\s.-]\d{4}\b/g

const mask = (value: string): string =>
  value.replace(EMAIL_VALUE, REDACTED).replace(PHONE_VALUE, REDACTED)

const ESCAPE_RUN = /(?:%[0-9A-Fa-f]{2})+/g

// URLs and form-encoded bodies escape @ and +, which hides PII from the raw patterns. Decode
// one escape run at a time: decoding the whole string throws on a literal % in free text
// ("50% off"), which would leave any encoded PII alongside it undecoded and so unmasked.
const decodeMaybe = (value: string): string =>
  value.replace(ESCAPE_RUN, (run) => {
    try {
      return decodeURIComponent(run)
    } catch {
      return run
    }
  })

// A key list only covers PII under a recognisable name. This catches the rest: an address in a
// note, a phone number quoted back in a validation message, an email in a free-text field.
export function maskPiiInString(value: string): string {
  const decoded = decodeMaybe(value)
  const maskedDecoded = mask(decoded)

  // Only fall back to the decoded form when it actually exposed PII, so ordinary
  // encoded strings reach Sentry unmangled.
  return maskedDecoded === decoded ? mask(value) : maskedDecoded
}

export function redactSensitive(value: unknown): unknown {
  if (typeof value === 'string') {
    try {
      const parsed = JSON.parse(value)
      if (parsed !== null && typeof parsed === 'object') {
        return JSON.stringify(redactSensitive(parsed))
      }
    } catch {
      // not JSON, fall through to the value scan
    }
    return maskPiiInString(value)
  }

  if (Array.isArray(value)) {
    return value.map(redactSensitive)
  }

  if (value !== null && typeof value === 'object') {
    const out: Record<string, unknown> = {}
    for (const [key, val] of Object.entries(value)) {
      const name = normalizeKey(key)

      // A boolean holds no personal data, so keys like includePhoneNumbers and
      // email_about_missed_calls stay readable even though their names match.
      const isSecret =
        typeof val !== 'boolean' &&
        !TIMESTAMP_KEY.test(name) &&
        (SENSITIVE_KEY.test(name) ||
          PII_KEY.test(name) ||
          PII_TOKEN.test(name) ||
          ADDRESS_KEY.test(name) ||
          PERSON_NAME_KEY.test(name))

      out[key] = isSecret ? REDACTED : redactSensitive(val)
    }
    return out
  }

  return value
}
